import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { Download, Loader2 } from "lucide-react";
import { type ReactNode, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { JobRun } from "~/components/job-log";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui";
import {
  GetBatchEvidencesJobDryRunResultDocument,
  type GetBatchEvidencesJobDryRunResultQuery,
} from "~/features/dataImportProjects/gqls";
import { useExportBatchEvidencesJobDryRunResult } from "~/features/dataImportProjects/hooks/useExportBatchEvidencesJobDryRunResult";
import { useTenant } from "~/features/tenant/context";
import { JsonKeyValues, type JsonRecord, stringifyValue } from "./BatchEvidencesJobJson";

type DryRunResult = Extract<
  GetBatchEvidencesJobDryRunResultQuery["cnutGetBatchEvidencesJobDryRunResult"],
  { __typename?: "DryRunBatchEvidencesJobOutput" }
>;
type DryRunEvidence = DryRunResult["evidences"][number];

type MetadataRowProps = {
  label: string;
  children: ReactNode;
};

const ITEMS_PER_PAGE = 100;

function MetadataRow({ label, children }: MetadataRowProps) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function JsonPanel({ title, value }: { title: string; value: JsonRecord }) {
  return (
    <section className="rounded-[var(--radius-md)] border border-border-default p-3">
      <h4 className="mb-2 text-12-bold text-fg-2">{title}</h4>
      <JsonKeyValues value={value} />
    </section>
  );
}

function DryRunEvidenceCard({ evidence, index }: { evidence: DryRunEvidence; index: number }) {
  const { t } = useTranslation();
  const diffRows = useMemo(() => {
    if (!evidence.diff) return [];
    return (["properties", "metadata", "citation", "wikipedia"] as const).flatMap((section) => {
      const changes = evidence.diff?.[section];
      if (!changes) return [];
      return Object.entries(changes).map(([key, change]) => {
        const entry = change as { before: unknown; after: unknown };
        return { field: `${section}.${key}`, before: entry.before, after: entry.after };
      });
    });
  }, [evidence.diff]);

  return (
    <article className="rounded-[var(--radius-md)] border border-border-default p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-14-bold text-fg-1">
          {t("model.evidence.modelName")} {index}
        </h3>
        <Badge tone={evidence.action === "create" ? "score-a" : "primary"}>{evidence.action}</Badge>
      </div>
      {evidence.action === "update" && diffRows.length > 0 ? (
        <div className="mb-4 overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
          <table className="w-full min-w-[680px] border-collapse text-12-regular">
            <thead className="bg-bg-subtle text-fg-2">
              <tr>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.field")}</th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.before")}</th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.after")}</th>
              </tr>
            </thead>
            <tbody>
              {diffRows.map((row) => (
                <tr key={row.field} className="border-t border-border-default first:border-t-0">
                  <td className="px-3 py-2 font-mono text-fg-2">{row.field}</td>
                  <td className="px-3 py-2 text-score-c">{stringifyValue(row.before)}</td>
                  <td className="px-3 py-2 text-score-a">{stringifyValue(row.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="grid gap-3 lg:grid-cols-3">
        <JsonPanel title={t("ui.properties")} value={evidence.properties as JsonRecord} />
        <JsonPanel title={t("model.common.metadata")} value={evidence.metadata as JsonRecord} />
        <JsonPanel title={t("model.evidence.citation")} value={evidence.citation as JsonRecord} />
      </div>
    </article>
  );
}

function DryRunResultPanel({ batchEvidencesJobRunId }: { batchEvidencesJobRunId: string }) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [page, setPage] = useState(1);
  const { data, dataState } = useSuspenseQuery(
    GetBatchEvidencesJobDryRunResultDocument,
    tenantId
      ? {
          variables: { input: { tenantId, jobRunId: batchEvidencesJobRunId } },
        }
      : skipToken,
  );

  const { onExport, exporting } = useExportBatchEvidencesJobDryRunResult({ jobRunId: batchEvidencesJobRunId });

  if (!tenantId || dataState !== "complete" || !data) {
    return null;
  }
  const result = data.cnutGetBatchEvidencesJobDryRunResult;
  if (result.__typename === "ValidationError") {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default p-4">
        <h3 className="text-14-bold text-fg-1">{t("feature.errors.errorFallback.errorDetails")}</h3>
        <dl className="mt-3 grid gap-2">
          <MetadataRow label={t("ui.code")}>{result.code}</MetadataRow>
          <MetadataRow label={t("ui.message")}>{result.message}</MetadataRow>
        </dl>
      </div>
    );
  }
  if (result.__typename !== "DryRunBatchEvidencesJobOutput") {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const totalPages = Math.ceil(result.evidences.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, result.evidences.length);
  const currentPageEvidences = result.evidences.slice(startIndex, endIndex);
  const referenceName = result.evidences[0]?.referenceName ?? "";
  const userName = result.evidences[0]?.userName || result.evidences[0]?.userEmail || "";

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-2 text-12-regular text-fg-2 sm:grid-cols-4">
          <MetadataRow label={t("model.batchEvidencesJobRun.evidencesCount")}>
            {result.evidences.length}
            {result.evidences.length > ITEMS_PER_PAGE ? ` (${t("ui.showing")} ${startIndex + 1}-${endIndex})` : ""}
          </MetadataRow>
          <MetadataRow label={t("model.batchEvidencesJobRun.skippedCount")}>{result.skippedCount}</MetadataRow>
          <MetadataRow label={t("model.reference.modelName")}>{referenceName || "-"}</MetadataRow>
          <MetadataRow label={t("model.user.modelName")}>{userName || "-"}</MetadataRow>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={exporting} onClick={onExport}>
          <Download className="size-3.5" />
          {t("ui.export")}
        </Button>
      </div>

      {currentPageEvidences.length > 0 ? (
        <div className="grid gap-4">
          {currentPageEvidences.map((evidence, index) => (
            <DryRunEvidenceCard key={`ev-${startIndex + index}`} evidence={evidence} index={startIndex + index + 1} />
          ))}
        </div>
      ) : (
        <div className="rounded-[var(--radius-md)] border border-border-default p-4">
          <h3 className="text-14-bold text-fg-1">{t("feature.batchEvidencesJobs.alert.noChangeRequestsCreated")}</h3>
          <p className="mt-1 text-12-regular text-fg-3">
            {t("feature.batchEvidencesJobs.alert.noChangeRequestsCreatedDescription")}
          </p>
        </div>
      )}

      {totalPages > 1 ? (
        <div className="flex justify-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            {t("ui.previous")}
          </Button>
          <span className="self-center text-12-regular text-fg-2">
            {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("ui.next")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export function DryRunResultDialog({
  open,
  onOpenChange,
  jobRun,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobRun: JobRun | null;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("model.batchEvidencesJob.modelName")}</DialogTitle>
          {jobRun ? (
            <DialogDescription>
              {t("model.common.id")}: {jobRun.jobRunId}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {jobRun ? (
          // Local Suspense boundary: keep useSuspenseQuery's suspension inside the dialog so it
          // doesn't bubble to the app-level boundary and blank/reload the page behind the dialog.
          <Suspense
            fallback={
              <div className="flex justify-center py-10">
                <Loader2 className="size-5 animate-spin text-fg-3" />
              </div>
            }
          >
            <DryRunResultPanel batchEvidencesJobRunId={jobRun.jobRunId} />
          </Suspense>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
